import React from 'react';
import { useLanguage } from '../context/LanguageContext';

/**
 * Single source of truth for invoice payment status rendering.
 *
 * Rules (match backend `payment_status`):
 *   paid         → remaining ≈ 0 and paid > 0
 *   paid_credit  → linked invoices fully paid BUT part of the payment amount
 *                  remains as unallocated credit (e.g. €150 received, €100
 *                  allocated, €50 still available for future invoices).
 *   partial      → paid > 0 and remaining > 0
 *   unpaid       → paid ≈ 0
 *
 * Pass either:
 *   - `status` directly ("paid" | "paid_credit" | "partial" | "unpaid"), OR
 *   - `amount` + `paidAmount` and the component will derive it.
 *
 * For `paid_credit`, pass `unallocatedAmount` to render the remaining credit
 * inside the badge tooltip.
 */
export function deriveInvoiceStatus(amount, paidAmount) {
  const total = Number(amount || 0);
  const paid = Number(paidAmount || 0);
  if (total <= 0 || paid >= total - 0.01) return 'paid';
  if (paid > 0.01) return 'partial';
  return 'unpaid';
}

const STYLES = {
  paid:         { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  paid_credit:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200'  },
  partial:      { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200'   },
  unpaid:       { bg: 'bg-zinc-50',    text: 'text-zinc-500',    border: 'border-zinc-200'    },
};

export default function InvoiceStatusBadge({ status, amount, paidAmount, unallocatedAmount, testId, className = '' }) {
  const { t } = useLanguage();
  const resolved = status || deriveInvoiceStatus(amount, paidAmount);
  const style = STYLES[resolved] || STYLES.unpaid;
  const label = resolved === 'paid' ? t('paid')
              : resolved === 'paid_credit' ? t('paidCredit')
              : resolved === 'partial' ? t('partial')
              : t('unpaid');
  const tip = resolved === 'paid_credit' && Number(unallocatedAmount) > 0
    ? `${t('availableCredit')}: €${Number(unallocatedAmount).toFixed(2)}`
    : undefined;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${style.bg} ${style.text} ${style.border} ${className}`}
      data-testid={testId || `invoice-status-${resolved}`}
      title={tip}
    >
      {label}
    </span>
  );
}
